import { CustomState } from "@bevco/Store/Reducers";
import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import {  Handler, makeHandlerChainAwaitable, replaceInChain } from "@insite/client-framework/HandlerCreator";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { chain, LoadRealTimeInventory, LoadRealTimePrices } from "@insite/client-framework/Store/Pages/ProductList/Handlers/LoadProductsPricingAndInventory";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { ProductModel, RealTimePricingModel } from "@insite/client-framework/Types/ApiModels";

let view = "List";
let tablePage = 1;

interface ProductPriceParameter {
    productId: string;
    unitOfMeasure: string;
    qtyOrdered: number;
    configuration?: string[];
}

replaceInChain(chain, LoadRealTimePrices, async props => {
    // Verndale Customization
    const state = props.getState();
    let { productInfosByProductId } = props.parameter;
    const products = props.parameter.products!;

    let productIds = Object.keys(productInfosByProductId).filter(o => !productInfosByProductId[o]?.pricing);
    if (productIds.length === 0) {
        return;
    }

    const productsDataView= getProductListDataView(state).value;
    let customProductInfosByProductId: ProductInfo[] = [];
    if(products && productInfosByProductId) {
        const productsProperties = products.map(product => {
            return {
                productId: product.id,
                properties: product.properties
            }
        });

        const productInfosArray = products.map(product => createFromProduct(product));
        customProductInfosByProductId = productInfosArray.map((productInfo) => {
            if (!productInfo?.productId) {
                return productInfo; 
            }
    
            const matchingProps = productsProperties.find(p => p.productId === productInfo.productId);
            const isCmiProduct = matchingProps?.properties?.isCmiProduct;
            const customerLength = matchingProps?.properties?.customerLength;
            const standardPutup = matchingProps?.properties?.standardPutup;
    
            const clonedInfo = { ...productInfo };
    
            if (String(isCmiProduct).toLowerCase() === "true") {
                if (typeof customerLength === "string" && customerLength !== "") {
                    clonedInfo.qtyOrdered = Number(customerLength.replace(/,/g, ""));
                } else if (typeof standardPutup === "string" && standardPutup !== "") {
                    clonedInfo.qtyOrdered = Number(standardPutup.replace(/,/g, ""));
                }
            }
    
            return clonedInfo;
        });
    }
    // END Verndale Customization

    productIds = customProductInfosByProductId.map(o => o.productId) as string[];

    const loadRealTimePricingParameter: Parameters<typeof loadRealTimePricing>[0] = {
        productPriceParameters: productIds.map(o => ({
            productId: o,
            unitOfMeasure: customProductInfosByProductId.find(p => p.productId === o)!.unitOfMeasure,
            qtyOrdered: customProductInfosByProductId.find(p => p.productId === o)!.qtyOrdered,
        })),
        onSuccess: realTimePricing => {
            props.dispatch({
                type: "Pages/ProductList/CompleteLoadRealTimePricing",
                realTimePricing,
            });
            props.dispatch({
                type: "Custom/ProductList/CompleteLoadRealTimePricing",
                realTimePricing,
            });

            props.pricingLoaded = true;
        },
        onError: () => {
            props.dispatch({
                type: "Pages/ProductList/FailedLoadRealTimePricing",
            });
            props.dispatch({
                type: "Custom/ProductList/FailedLoadRealTimePricing",
            });
            props.pricingLoaded = true;
        },
        onComplete(realTimePricingProps) {
            if (realTimePricingProps.apiResult) {
                this.onSuccess?.(realTimePricingProps.apiResult);
            } else if (realTimePricingProps.error) {
                this.onError?.(realTimePricingProps.error);
            }
        },
    };

    const settings = getSettingsCollection(props.getState());

    if (IS_SERVER_SIDE && getIsWebCrawler() && !settings.websiteSettings.includeRealTimeInventoryAndPricingForSeo) {
        const awaitableLoadRealTimePricing = makeHandlerChainAwaitable<
            Parameters<typeof loadRealTimePricing>[0],
            RealTimePricingModel
        >(loadRealTimePricing);
        const realTimePricing = await awaitableLoadRealTimePricing({
            productPriceParameters: loadRealTimePricingParameter.productPriceParameters,
        })(props.dispatch, props.getState);
        if (realTimePricing) {
            loadRealTimePricingParameter.onSuccess?.(realTimePricing);
        } else {
            loadRealTimePricingParameter.onError?.(realTimePricing);
        }
    } else {
        props.dispatch(loadRealTimePricing(loadRealTimePricingParameter));
    }

    const productSettings = settings.productSettings;
    if (productSettings.canSeePrices && productSettings.inventoryIncludedWithPricing) {
        await waitFor(() => !!props.pricingLoaded);
    }
})

replaceInChain(chain, LoadRealTimeInventory, props => {
    // Verndale Customization
     // Only send the new products from the current page, not the accumulated ones
     const products = props.parameter.products!;
     // END Verndale Customization

    if (
        !products?.length ||
        (getIsWebCrawler() &&
            !getSettingsCollection(props.getState()).websiteSettings.includeRealTimeInventoryAndPricingForSeo)
    ) {
        return;
    }

    const { productInfosByProductId } = props.parameter;

    const productIds = products.map(o => o.id);
    if (productIds.length === 0) {
        return;
    }

    props.dispatch(
        loadRealTimeInventory({
            productIds,
            onSuccess: realTimeInventory => {
                props.dispatch({
                    type: "Pages/ProductList/CompleteLoadRealTimeInventory",
                    realTimeInventory,
                });
                props.dispatch({
                    type: "Custom/ProductList/CompleteLoadRealTimeInventory",
                    realTimeInventory,
                });
            },
            onError: () => {
                props.dispatch({
                    type: "Pages/ProductList/FailedLoadRealTimeInventory",
                });
                props.dispatch({
                    type: "Custom/ProductList/FailedLoadRealTimeInventory",
                });
            },
            onComplete(realTimeInventoryProps) {
                if (realTimeInventoryProps.apiResult) {
                    this.onSuccess?.(realTimeInventoryProps.apiResult);
                } else if (realTimeInventoryProps.error) {
                    this.onError?.(realTimeInventoryProps.error);
                }
            },
        }),
    );

})

